import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { executionService } from '../config/api';
import ExecutionTimeline from '../components/ExecutionTimeline';

// Icons
const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const StopIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const ChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    PENDING: { color: 'badge-warning', text: 'Pending' },
    SCHEDULED: { color: 'badge-info', text: 'Scheduled' },
    STARTED: { color: 'badge-info', text: 'Starting' },
    RUNNING: { color: 'badge-success', text: 'Running' },
    STOPPING: { color: 'badge-warning', text: 'Stopping' },
    STOPPED: { color: 'badge-neutral', text: 'Stopped' },
    COMPLETED: { color: 'badge-primary', text: 'Completed' },
    FAILED: { color: 'badge-error', text: 'Failed' },
    STALLED: { color: 'badge-error', text: 'Stalled' },
  };

  const config = statusConfig[status] || { color: 'badge-ghost', text: status };

  return (
    <span className={`badge ${config.color} badge-sm`}>
      {config.text}
    </span>
  );
};

// Execution List Component
const ExecutionList = ({ executions, onSelect, onStop, onRefresh, loading }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="card-title text-2xl">Live Trading</h2>
        <button className="btn btn-ghost btn-sm gap-2" onClick={onRefresh} disabled={loading}>
          <RefreshIcon />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {executions.length === 0 ? (
        <div className="text-center py-12">
          <ChartIcon />
          <p className="text-base-content/60 mt-4">No active executions</p>
          <p className="text-sm text-base-content/40 mt-2">
            Start a strategy from the Strategies page to begin live trading
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Strategy</th>
                <th>Instrument</th>
                <th>Broker</th>
                <th>Status</th>
                <th>Signals</th>
                <th>Orders</th>
                <th>Started</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((exec) => (
                <tr key={exec.id} className="hover cursor-pointer" onClick={() => onSelect(exec.id)}>
                  <td className="font-medium">{exec.strategyName || exec.strategyId}</td>
                  <td>{exec.instrumentKey?.split('|')[1] || exec.instrumentKey}</td>
                  <td>{exec.broker}</td>
                  <td><StatusBadge status={exec.status} /></td>
                  <td>{exec.totalSignals || 0}</td>
                  <td>{exec.totalOrders || 0}</td>
                  <td>{new Date(exec.createdAt).toLocaleString()}</td>
                  <td>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      {['RUNNING', 'SCHEDULED', 'PENDING'].includes(exec.status) && (
                        <button
                          className="btn btn-error btn-xs gap-1"
                          onClick={() => onStop(exec.id)}
                        >
                          <StopIcon />
                          Stop
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={() => onSelect(exec.id)}
                      >
                        Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Execution Detail Component
const ExecutionDetail = ({ executionId, onBack }) => {
  const [execution, setExecution] = useState(null);
  const [positions, setPositions] = useState([]);
  const [signals, setSignals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [execRes, posRes, pnlRes] = await Promise.all([
        executionService.get(executionId),
        executionService.getPositions(executionId),
        executionService.getPnl(executionId),
      ]);
      setExecution(execRes.data);
      setPositions(posRes.data || []);
      setPnl(pnlRes.data);
    } catch (error) {
      console.error('Error fetching execution details:', error);
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  const fetchSignals = useCallback(async () => {
    try {
      const res = await executionService.getSignals(executionId);
      setSignals(res.data || []);
    } catch (error) {
      console.error('Error fetching signals:', error);
    }
  }, [executionId]);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await executionService.getOrders(executionId);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [executionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'signals') fetchSignals();
    if (activeTab === 'orders') fetchOrders();
  }, [activeTab, fetchSignals, fetchOrders]);

  // Auto-refresh for running executions
  useEffect(() => {
    if (execution?.status === 'RUNNING') {
      const interval = setInterval(fetchData, 5000);
      return () => clearInterval(interval);
    }
  }, [execution?.status, fetchData]);

  const handleStop = async () => {
    try {
      await executionService.stop(executionId);
      fetchData();
    } catch (error) {
      console.error('Error stopping execution:', error);
    }
  };

  if (loading && !execution) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="text-center py-12">
        <p className="text-error">Execution not found</p>
        <button className="btn btn-ghost mt-4" onClick={onBack}>Go Back</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button className="btn btn-ghost btn-sm gap-2" onClick={onBack}>
          <BackIcon />
          Back
        </button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold">{execution.strategyName || execution.strategyId}</h2>
          <p className="text-base-content/60">{execution.instrumentKey}</p>
        </div>
        <StatusBadge status={execution.status} />
        {['RUNNING', 'SCHEDULED', 'PENDING'].includes(execution.status) && (
          <button className="btn btn-error btn-sm gap-2" onClick={handleStop}>
            <StopIcon />
            Stop
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Total P&L</div>
          <div className={`stat-value text-lg ${(pnl?.totalPnl || 0) >= 0 ? 'text-success' : 'text-error'}`}>
            ₹{(pnl?.totalPnl || 0).toFixed(2)}
          </div>
          <div className="stat-desc">Realized: ₹{(pnl?.realizedPnl || 0).toFixed(2)}</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Signals</div>
          <div className="stat-value text-lg">{execution.totalSignals || 0}</div>
          <div className="stat-desc">Generated</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Orders</div>
          <div className="stat-value text-lg">{execution.totalOrders || 0}</div>
          <div className="stat-desc">Placed</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Broker</div>
          <div className="stat-value text-lg">{execution.broker}</div>
          <div className="stat-desc">Connected</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs tabs-boxed mb-4">
        <a className={`tab ${activeTab === 'overview' ? 'tab-active' : ''}`} onClick={() => setActiveTab('overview')}>
          Overview
        </a>
        <a className={`tab ${activeTab === 'timeline' ? 'tab-active' : ''}`} onClick={() => setActiveTab('timeline')}>
          Timeline
        </a>
        <a className={`tab ${activeTab === 'positions' ? 'tab-active' : ''}`} onClick={() => setActiveTab('positions')}>
          Positions ({positions.length})
        </a>
        <a className={`tab ${activeTab === 'signals' ? 'tab-active' : ''}`} onClick={() => setActiveTab('signals')}>
          Signals
        </a>
        <a className={`tab ${activeTab === 'orders' ? 'tab-active' : ''}`} onClick={() => setActiveTab('orders')}>
          Orders
        </a>
      </div>

      {/* Tab Content */}
      <div className="bg-base-100 rounded-lg p-4 shadow">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-base-content/60">Execution ID</p>
              <p className="font-mono text-sm">{execution.id}</p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">Strategy ID</p>
              <p className="font-mono text-sm">{execution.strategyId}</p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">Start Date</p>
              <p>{execution.startDate ? new Date(execution.startDate).toLocaleString() : 'Immediate'}</p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">End Date</p>
              <p>{execution.endDate ? new Date(execution.endDate).toLocaleString() : 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">Created At</p>
              <p>{new Date(execution.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">Last Heartbeat</p>
              <p>{execution.lastHeartbeat ? new Date(execution.lastHeartbeat).toLocaleString() : 'N/A'}</p>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <ExecutionTimeline
            executionId={executionId}
            autoRefresh={execution?.status === 'RUNNING'}
            refreshInterval={5000}
          />
        )}

        {activeTab === 'positions' && (
          <div className="overflow-x-auto">
            {positions.length === 0 ? (
              <p className="text-center py-8 text-base-content/60">No positions</p>
            ) : (
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th>Instrument</th>
                    <th>Qty</th>
                    <th>Avg Price</th>
                    <th>Current</th>
                    <th>Unrealized P&L</th>
                    <th>Realized P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((pos) => (
                    <tr key={pos.id}>
                      <td>{pos.instrumentKey?.split('|')[1] || pos.instrumentKey}</td>
                      <td className={pos.quantity > 0 ? 'text-success' : pos.quantity < 0 ? 'text-error' : ''}>
                        {pos.quantity}
                      </td>
                      <td>₹{pos.averagePrice?.toFixed(2)}</td>
                      <td>₹{pos.currentPrice?.toFixed(2)}</td>
                      <td className={pos.unrealizedPnl >= 0 ? 'text-success' : 'text-error'}>
                        ₹{pos.unrealizedPnl?.toFixed(2)}
                      </td>
                      <td className={pos.realizedPnl >= 0 ? 'text-success' : 'text-error'}>
                        ₹{pos.realizedPnl?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'signals' && (
          <div className="overflow-x-auto">
            {signals.length === 0 ? (
              <p className="text-center py-8 text-base-content/60">No signals generated yet</p>
            ) : (
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Action</th>
                    <th>Price</th>
                    <th>Rule Group</th>
                    <th>Order Placed</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.map((signal) => (
                    <tr key={signal.id}>
                      <td>{new Date(signal.generatedAt).toLocaleString()}</td>
                      <td>{signal.signalType}</td>
                      <td className={signal.action === 'BUY' ? 'text-success' : 'text-error'}>
                        {signal.action}
                      </td>
                      <td>₹{signal.triggerPrice?.toFixed(2)}</td>
                      <td className="text-xs">{signal.conditionDetails || signal.ruleGroupId}</td>
                      <td>{signal.orderPlaced ? '✓' : '✗'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="overflow-x-auto">
            {orders.length === 0 ? (
              <p className="text-center py-8 text-base-content/60">No orders placed yet</p>
            ) : (
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Order ID</th>
                    <th>Type</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Filled</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>{new Date(order.placedAt).toLocaleString()}</td>
                      <td className="font-mono text-xs">{order.orderId || '-'}</td>
                      <td className={order.transactionType === 'BUY' ? 'text-success' : 'text-error'}>
                        {order.transactionType}
                      </td>
                      <td>{order.quantity}</td>
                      <td>₹{order.price?.toFixed(2) || 'MKT'}</td>
                      <td>₹{order.filledPrice?.toFixed(2) || '-'}</td>
                      <td>
                        <span className={`badge badge-sm ${
                          order.status === 'FILLED' ? 'badge-success' :
                          order.status === 'REJECTED' ? 'badge-error' :
                          order.status === 'PENDING' ? 'badge-warning' : 'badge-ghost'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main LiveTrading Page
const LiveTrading = () => {
  const [executions, setExecutions] = useState([]);
  const [selectedExecutionId, setSelectedExecutionId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchExecutions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await executionService.list();
      setExecutions(response.data || []);
    } catch (error) {
      console.error('Error fetching executions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  const handleStop = async (executionId) => {
    try {
      await executionService.stop(executionId);
      fetchExecutions();
    } catch (error) {
      console.error('Error stopping execution:', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 h-full overflow-auto">
        <div className="card bg-base-100 shadow-lg w-full min-h-full">
          <div className="card-body">
            {selectedExecutionId ? (
              <ExecutionDetail
                executionId={selectedExecutionId}
                onBack={() => setSelectedExecutionId(null)}
              />
            ) : (
              <ExecutionList
                executions={executions}
                onSelect={setSelectedExecutionId}
                onStop={handleStop}
                onRefresh={fetchExecutions}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LiveTrading;
